import { useSelector } from "react-redux";
import { Box, Button, Stepper, Step, StepLabel, useTheme } from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import { tokens } from "../../theme";
import Payment from "./store-components/Payment";
import Shipping from "./store-components/Shipping";
import { loadStripe } from "@stripe/stripe-js";
import Navbar from "./global/Navbar";
import { useCreateOrderMutation } from "../../features/orders/ordersApiSlice";
import Footer from "./global/Footer";

const stripePromise = loadStripe(
  "pk_test_51LgU7yConHioZHhlAcZdfDAnV9643a7N1CMpxlKtzI1AUWLsRyrord79GYzZQ6m8RzVnVQaHsgbvN1qSpiDegoPi006QkO0Mlc"
);

const Checkout = () => {
  const [activeStep, setActiveStep] = useState(0);
  const cart = useSelector((state) => state.cart.cart);
  const isFirstStep = activeStep === 0;
  const isSecondStep = activeStep === 1;
  const [createOrder] = useCreateOrderMutation(); // Initialize the mutation hook

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  async function makePayment(values) {
    const stripe = await stripePromise;
    const requestBody = {
      userName: [values.firstName, values.lastName].join(" "),
      email: values.email,
      products: cart.map(({ id, count }) => ({
        id,
        count,
      })),
    };

    // Use the mutation hook instead of fetch
    try {
      const { data: session } = await createOrder(requestBody).unwrap();
      await stripe.redirectToCheckout({
        sessionId: session.id,
      });
    } catch (error) {
      console.error('Failed to create order:', error);
    }
  }

  const handleFormSubmit = async (values, actions) => {
    setActiveStep(activeStep + 1);

    // this copies the billing address onto shipping address
    if (isFirstStep && values.shippingAddress.isSameAddress) {
      actions.setFieldValue("shippingAddress", {
        ...values.billingAddress,
        isSameAddress: true,
      });
    }

    if (isSecondStep) {
      makePayment(values);
    }

    actions.setTouched({});
  };

  async function makePayment(values) {
    const stripe = await stripePromise;
    const requestBody = {
      userName: [values.firstName, values.lastName].join(" "),
      email: values.email,
      products: cart.map(({ id, count }) => ({
        id,
        count,
      })),
    };

    const response = await fetch("http://localhost:3500/api/orders", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });
    const session = await response.json();
    await stripe.redirectToCheckout({
      sessionId: session.id,
    });
  }

  return (
    <Box width="100%">
      <Navbar />
      <Box width="80%" m="100px auto">
        <Stepper activeStep={activeStep} sx={{ m: "20px 0" }}>
          <Step>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  color: colors.primary[900], // Color for the label text
                  fontFamily: 'Poppins, sans-serif', // Font family for the label text
                },
                '& .MuiStepLabel-active': {
                  color: colors.primary[900], // Color for the label text when active
                  fontFamily: 'Poppins, sans-serif', // Font family for the label text when active
                },
                '& .MuiStepLabel-completed': {
                  color: colors.primary[900], // Color for the label text when completed
                  fontFamily: 'Poppins, sans-serif', // Font family for the label text when completed
                }
              }}
            >
              Billing
            </StepLabel>
          </Step>
          <Step>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  color: colors.primary[900], // Color for the label text
                  fontFamily: 'Poppins, sans-serif', // Font family for the label text
                },
                '& .MuiStepLabel-active': {
                  color: colors.primary[900], // Color for the label text when active
                  fontFamily: 'Poppins, sans-serif', // Font family for the label text when active
                },
                '& .MuiStepLabel-completed': {
                  color: colors.primary[900], // Color for the label text when completed
                  fontFamily: 'Poppins, sans-serif', // Font family for the label text when completed
                }
              }}
            >
              Payment
            </StepLabel>
          </Step>
        </Stepper>
        <Box>
          <Formik
            onSubmit={handleFormSubmit}
            initialValues={initialValues}
            validationSchema={checkoutSchema[activeStep]}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                {isFirstStep && (
                  <Shipping
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                  />
                )}
                {isSecondStep && (
                  <Payment
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                  />
                )}
                <Box display="flex" justifyContent="space-between" gap="50px">
                  {!isFirstStep && (
                    <Button
                      fullWidth
                      color="primary"
                      variant="contained"
                      sx={{
                        backgroundColor: colors.primary[200],
                        boxShadow: "none",
                        color: "white",
                        borderRadius: 0,
                        padding: "15px 40px",
                      }}
                      onClick={() => setActiveStep(activeStep - 1)}
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    fullWidth
                    type="submit"
                    color="primary"
                    disabled={true}
                    variant="contained"
                    sx={{
                      backgroundColor: colors.primary[600],
                                color: colors.greenAccent[400],
                                borderRadius: '10px', // Adding rounded corners
                                minWidth: "100%",
                                padding: "20px 40px",
                                m: "20px 0",
                                fontWeight: 'bold', // Making the text bold
                                fontSize: '1.1rem', // Increasing the font size
                                fontFamily: 'Poppins, sans-serif', // Using Poppins font
                    }}
                  >
                    {!isSecondStep ? "Coming Soon" : "Coming Soon" /* "Next" : "Place Order"*/ } 
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

const initialValues = {
  billingAddress: {
    firstName: "",
    lastName: "",
    country: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
  },
  shippingAddress: {
    isSameAddress: true,
    firstName: "",
    lastName: "",
    country: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
  },
  email: "",
  phoneNumber: "",
};

const checkoutSchema = [
  yup.object().shape({
    billingAddress: yup.object().shape({
      firstName: yup.string().required("required"),
      lastName: yup.string().required("required"),
      country: yup.string().required("required"),
      street1: yup.string().required("required"),
      street2: yup.string(),
      city: yup.string().required("required"),
      state: yup.string().required("required"),
      zipCode: yup.string().required("required"),
    }),
    shippingAddress: yup.object().shape({
      isSameAddress: yup.boolean(),
      firstName: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      lastName: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      country: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      street1: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      street2: yup.string(),
      city: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      state: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
      zipCode: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
      }),
    }),
  }),
  yup.object().shape({
    email: yup.string().required("required"),
    phoneNumber: yup.string().required("required"),
  }),
];

export default Checkout;